import { ref, computed, nextTick } from "vue";
import { getArtworkImageSrc } from "~/utils";
import { ENABLE_ARTWORK_SHARED_TRANSITION } from "~/constants";

// Global client state for currently transitioning artwork
const activeArtwork = ref<any | null>(null);
const activeArtworkId = ref<string | null>(null);
const activeArtworkSlug = ref<string | null>(null);
const activeCardKey = ref<string | null>(null);
const isTransitionActive = ref(false);
const isTransitionReady = ref(false);
let cleanupTimeout: any = null;

export function preloadArtworkHiRes(photoName?: string) {
  if (!ENABLE_ARTWORK_SHARED_TRANSITION || !import.meta.client || !photoName) return;
  try {
    const url = getArtworkImageSrc(photoName, 1920);
    const img = new Image();
    img.src = url;
  } catch (_) {}
}

export function useArtworkViewTransition() {
  const supportsViewTransitions = () => {
    if (!ENABLE_ARTWORK_SHARED_TRANSITION) return false;
    if (!import.meta.client) return false;
    if (!("startViewTransition" in document)) return false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    return true;
  };

  /**
   * Pre-binds the view-transition-name on pointerdown so the DOM snapshot captures it reliably
   * and preloads the hi-res image
   */
  const prepareArtworkTransition = (artwork: any, cardKey?: string) => {
    if (!supportsViewTransitions()) return;
    if (cleanupTimeout) clearTimeout(cleanupTimeout);

    const idStr = artwork?.id != null ? String(artwork.id) : "";
    const slugStr = artwork?.slug != null ? String(artwork.slug) : null;
    const photo = artwork?.photo_names?.[0] || artwork?.photo_name;

    if (import.meta.dev) {
      console.log(`[Artwork Transition] click (t=${Math.round(performance.now())}ms)`, {
        id: idStr,
        slug: slugStr,
        cardKey,
        thumbnailAvailable: photo ? getArtworkImageSrc(photo, 360) : false,
      });
    }

    if (photo) {
      preloadArtworkHiRes(photo);
    }

    activeArtwork.value = artwork;
    activeArtworkId.value = idStr;
    activeArtworkSlug.value = slugStr;
    activeCardKey.value = cardKey || idStr;
    isTransitionActive.value = true;
    isTransitionReady.value = false;

    // Safety fallback cleanup in case view transition was cancelled
    cleanupTimeout = setTimeout(() => {
      clearTransition();
    }, 3000);
  };

  /**
   * Triggers a shared element view transition when navigating to an artwork detail page.
   * Nuxt experimental.viewTransition handles the router startViewTransition wrapping.
   */
  const startArtworkTransition = async (
    artwork: any,
    navigate: () => Promise<any> | any,
    cardKey?: string
  ) => {
    prepareArtworkTransition(artwork, cardKey);
    await nextTick();
    if (import.meta.dev) {
      console.log(`[Artwork Transition] start (t=${Math.round(performance.now())}ms)`);
    }
    return await navigate();
  };

  /**
   * Called by the Nuxt view-transition client plugin when startViewTransition executes.
   */
  const onTransitionStart = (transition: any) => {
    if (!isTransitionActive.value) return;
    if (import.meta.dev) {
      console.log(`[Artwork Transition] startViewTransition (t=${Math.round(performance.now())}ms)`);
    }
    if (transition?.updateCallbackDone) {
      transition.updateCallbackDone
        .then(() => {
          if (import.meta.dev) {
            console.log(`[Artwork Transition] updateCallbackDone (t=${Math.round(performance.now())}ms)`);
          }
        })
        .catch(() => {});
    }
    if (transition?.ready) {
      transition.ready
        .then(() => {
          isTransitionReady.value = true;
          if (import.meta.dev) {
            console.log(`[Artwork Transition] ready (t=${Math.round(performance.now())}ms)`);
          }
        })
        .catch(() => {});
    }
    if (transition?.finished) {
      transition.finished
        .then(() => {
          if (import.meta.dev) {
            console.log(`[Artwork Transition] finished (t=${Math.round(performance.now())}ms)`);
          }
          clearTransition();
        })
        .catch(() => {
          clearTransition();
        });
    }
  };

  /**
   * Checks if this specific artwork/card is currently part of an active transition on the listing page.
   */
  const isArtworkActive = (artworkOrIdentifier: any, cardKey?: string) => {
    if (!artworkOrIdentifier || !isTransitionActive.value) return false;
    if (cardKey && activeCardKey.value) {
      return cardKey === activeCardKey.value;
    }
    if (typeof artworkOrIdentifier === "object") {
      const id = artworkOrIdentifier.id != null ? String(artworkOrIdentifier.id) : null;
      const slug = artworkOrIdentifier.slug != null ? String(artworkOrIdentifier.slug) : null;
      return Boolean(
        (id && (activeArtworkId.value === id || activeArtworkSlug.value === id)) ||
        (slug && (activeArtworkId.value === slug || activeArtworkSlug.value === slug))
      );
    }
    const str = String(artworkOrIdentifier);
    return Boolean(
      (activeArtworkId.value && activeArtworkId.value === str) ||
      (activeArtworkSlug.value && activeArtworkSlug.value === str)
    );
  };

  /**
   * Returns inline style assigning view-transition-name for source listing cards.
   */
  const getTransitionStyle = (artworkOrIdentifier: any, cardKey?: string) => {
    if (isArtworkActive(artworkOrIdentifier, cardKey)) {
      return {
        viewTransitionName: "artwork-hero-transition",
        "view-transition-name": "artwork-hero-transition",
      };
    }
    return {};
  };

  /**
   * Stable transition style for the single destination hero image on the detail page.
   * Completely independent of mutable artwork async data objects during active animation.
   */
  const getHeroTransitionStyle = () => {
    if (isTransitionActive.value) {
      return {
        viewTransitionName: "artwork-hero-transition",
        "view-transition-name": "artwork-hero-transition",
      };
    }
    return {};
  };

  /**
   * Returns preloaded artwork data if available during transition
   */
  const getPrefetchedArtwork = (slugOrId?: string) => {
    if (!activeArtwork.value) return undefined;
    const a = activeArtwork.value;
    if (!slugOrId) return a;
    const str = String(slugOrId);
    if (String(a.id) === str || a.slug === str || !a.slug) {
      return {
        ...a,
        photo_name: a.photo_name || a.photo_names?.[0] || "",
        photo_width: a.photo_width || a.photo_widths?.[0] || 1200,
        photo_height: a.photo_height || a.photo_heights?.[0] || 900,
      };
    }
    return undefined;
  };

  const clearTransition = () => {
    if (cleanupTimeout) clearTimeout(cleanupTimeout);
    if (import.meta.dev && isTransitionActive.value) {
      console.log(`[Artwork Transition] cleanup (t=${Math.round(performance.now())}ms)`);
    }
    activeArtworkId.value = null;
    activeArtworkSlug.value = null;
    activeArtwork.value = null;
    activeCardKey.value = null;
    isTransitionActive.value = false;
    isTransitionReady.value = false;
  };

  return {
    activeArtworkId: computed(() => activeArtworkId.value),
    activeArtworkSlug: computed(() => activeArtworkSlug.value),
    isTransitionActive: computed(() => isTransitionActive.value),
    isTransitionReady: computed(() => isTransitionReady.value),
    supportsViewTransitions,
    prepareArtworkTransition,
    startArtworkTransition,
    onTransitionStart,
    preloadArtworkHiRes,
    getTransitionStyle,
    getHeroTransitionStyle,
    isArtworkActive,
    getPrefetchedArtwork,
    clearTransition,
  };
}

